import React from "react";

/* ---------- Projection ----------
   Simple equirectangular, with longitude scaled by ~cos(22°)
   so India isn't stretched. viewBox is 0 0 600 670. */
const LON0 = 68;
const LAT_TOP = 37.2;
const project = ([lon, lat]) => [
  +((lon - LON0) * 20).toFixed(1),
  +((LAT_TOP - lat) * 21.5).toFixed(1),
];

const toPath = (pts) =>
  pts
    .map((p, i) => {
      const [x, y] = project(p);
      return `${i ? "L" : "M"}${x} ${y}`;
    })
    .join(" ") + " Z";

/* ---------- India outline: [lon, lat], clockwise ---------- */
const INDIA = [
  // Pakistan border: Kutch -> Rajasthan -> Punjab
  [68.17, 23.72],
  [68.62, 24.3],
  [69.58, 24.28],
  [70.12, 24.28],
  [71.03, 24.62],
  [70.55, 25.2],
  [70.0, 25.95],
  [69.8, 26.6],
  [70.05, 27.35],
  [70.25, 27.95],
  [71.1, 28.4],
  [72.0, 28.95],
  [72.9, 29.45],
  [73.45, 29.95],
  [73.95, 30.35],
  [74.55, 31.05],
  [74.6, 31.6],
  [74.75, 32.0],
  [75.2, 32.4],
  // Jammu & Kashmir, PoK, Gilgit-Baltistan (official Indian depiction)
  [74.6, 32.75],
  [74.15, 33.05],
  [73.75, 33.15],
  [73.55, 33.9],
  [73.8, 34.6],
  [73.25, 35.1],
  [72.95, 35.85],
  [73.55, 36.45],
  [74.55, 37.02],
  [75.4, 36.95],
  [75.95, 36.7],
  [76.65, 35.95],
  [77.8, 35.45],
  // Ladakh / Aksai Chin
  [78.1, 35.8],
  [79.0, 35.95],
  [80.25, 35.45],
  [80.35, 34.85],
  [79.9, 34.3],
  [79.5, 33.6],
  [79.35, 32.7],
  // Ladakh / Himachal / Uttarakhand -> Tibet
  [78.95, 32.3],
  [78.75, 31.85],
  [78.75, 31.3],
  [79.05, 31.05],
  [79.9, 31.0],
  [80.25, 30.6],
  [80.55, 30.45],
  // Nepal border
  [80.55, 29.85],
  [80.25, 29.2],
  [80.05, 28.85],
  [80.5, 28.65],
  [81.2, 28.35],
  [81.9, 27.9],
  [82.7, 27.55],
  [83.4, 27.4],
  [84.0, 27.35],
  [84.7, 27.1],
  [85.0, 26.75],
  [86.0, 26.62],
  [87.2, 26.4],
  [88.15, 26.45],
  [88.05, 26.95],
  [88.0, 27.45],
  [88.15, 27.95],
  // Sikkim
  [88.55, 28.1],
  [88.85, 27.85],
  [88.9, 27.35],
  [88.85, 26.95],
  // Bhutan border
  [89.0, 26.75],
  [89.6, 26.72],
  [90.4, 26.85],
  [91.0, 26.85],
  [91.65, 26.8],
  [92.1, 26.88],
  [92.05, 27.35],
  [91.6, 27.8],
  [91.65, 28.1],
  // Arunachal Pradesh -> China
  [92.1, 28.2],
  [92.7, 28.15],
  [93.5, 28.65],
  [94.3, 29.25],
  [95.2, 29.3],
  [96.0, 29.35],
  [96.4, 28.9],
  [96.9, 28.35],
  [97.35, 28.15],
  [97.4, 27.85],
  // Myanmar border: Arunachal -> Nagaland -> Manipur -> Mizoram
  [97.2, 27.45],
  [96.6, 27.3],
  [96.2, 27.2],
  [95.6, 27.0],
  [95.2, 26.7],
  [95.15, 26.2],
  [94.8, 25.7],
  [94.6, 25.3],
  [94.7, 24.8],
  [94.4, 24.3],
  [94.05, 23.9],
  [93.55, 24.0],
  [93.4, 23.5],
  [93.25, 23.0],
  [93.05, 22.4],
  [92.6, 21.98],
  // Bangladesh border: Mizoram -> Tripura -> Meghalaya -> Assam -> Bengal
  [92.35, 22.8],
  [92.25, 23.4],
  [91.85, 23.1],
  [91.55, 22.95],
  [91.25, 23.2],
  [91.15, 23.65],
  [91.45, 24.1],
  [91.8, 24.35],
  [92.15, 24.45],
  [92.25, 24.95],
  [91.85, 25.15],
  [91.4, 25.17],
  [90.8, 25.17],
  [90.2, 25.2],
  [89.85, 25.3],
  [89.85, 25.9],
  [89.7, 26.2],
  [89.2, 26.25],
  [88.75, 26.35],
  [88.4, 26.62],
  [88.35, 26.2],
  [88.5, 25.8],
  [88.45, 25.3],
  [88.1, 24.85],
  [88.2, 24.5],
  [88.6, 24.2],
  [88.7, 23.85],
  [88.75, 23.4],
  [88.95, 23.1],
  [89.05, 22.75],
  [88.85, 22.4],
  [89.0, 21.85],
  [88.9, 21.6],
  // East coast: Bengal -> Odisha -> Andhra -> Tamil Nadu
  [88.2, 21.65],
  [87.55, 21.62],
  [86.95, 21.45],
  [86.9, 20.75],
  [86.65, 20.3],
  [86.1, 19.9],
  [85.85, 19.78],
  [85.5, 19.65],
  [84.9, 19.27],
  [84.0, 18.4],
  [83.3, 17.7],
  [82.3, 16.95],
  [81.8, 16.6],
  [81.15, 16.15],
  [81.0, 15.85],
  [80.45, 15.85],
  [80.05, 15.5],
  [80.1, 14.45],
  [80.3, 13.6],
  [80.28, 13.1],
  [80.2, 12.6],
  [79.85, 11.9],
  [79.8, 11.7],
  [79.85, 10.75],
  [79.85, 10.3],
  [79.5, 10.35],
  [79.2, 10.0],
  [79.15, 9.3],
  [78.7, 9.2],
  [78.4, 9.0],
  [78.2, 8.8],
  [78.1, 8.5],
  [77.55, 8.08],
  // West coast: Kerala -> Karnataka -> Goa -> Maharashtra -> Gujarat
  [77.25, 8.17],
  [76.95, 8.5],
  [76.55, 8.9],
  [76.3, 9.5],
  [76.2, 9.95],
  [76.05, 10.5],
  [75.75, 11.25],
  [75.4, 11.85],
  [74.95, 12.5],
  [74.8, 12.9],
  [74.7, 13.35],
  [74.1, 14.8],
  [73.8, 15.5],
  [73.3, 17.0],
  [72.9, 18.65],
  [72.8, 18.95],
  [72.7, 19.95],
  [72.9, 20.6],
  [72.7, 21.1],
  [72.7, 21.65],
  [72.55, 22.3],
  [72.2, 21.75],
  [72.2, 21.4],
  [71.0, 20.7],
  [70.35, 20.9],
  [69.6, 21.6],
  [68.95, 22.25],
  [69.07, 22.47],
  // Gulf of Kutch and Kutch coast
  [69.7, 22.45],
  [70.25, 22.95],
  [69.7, 22.9],
  [69.35, 22.83],
  [68.9, 23.05],
  [68.5, 23.4],
];

const ISLANDS = [
  // Andaman
  [
    [92.95, 13.6],
    [93.05, 13.2],
    [92.95, 12.7],
    [92.95, 12.2],
    [92.85, 11.7],
    [92.65, 11.6],
    [92.65, 12.2],
    [92.7, 12.8],
    [92.85, 13.3],
  ],
  // Little Andaman
  [
    [92.6, 10.85],
    [92.72, 10.7],
    [92.62, 10.5],
    [92.5, 10.65],
  ],
  // Car Nicobar
  [
    [92.75, 9.28],
    [92.88, 9.15],
    [92.76, 9.08],
  ],
  // Central Nicobar
  [
    [93.45, 8.2],
    [93.62, 8.0],
    [93.48, 7.95],
  ],
  // Great Nicobar
  [
    [93.7, 7.35],
    [93.95, 7.1],
    [93.85, 6.8],
    [93.7, 6.78],
    [93.65, 7.1],
  ],
];

const INDIA_PATH = toPath(INDIA);
const ISLAND_PATHS = ISLANDS.map(toPath);

/* ---------- Cities: real [lon, lat] ---------- */
const ORIGIN = (() => {
  const [x, y] = project([76.65, 10.78]);
  return { name: "Palakkad", x, y };
})();

const DESTINATIONS = [
  { name: "Srinagar", ll: [74.8, 34.08], a: "end" },
  { name: "Chandigarh", ll: [76.78, 30.73], a: "start" },
  { name: "Delhi", ll: [77.21, 28.61], a: "start" },
  { name: "Jaipur", ll: [75.79, 26.91], a: "end" },
  { name: "Lucknow", ll: [80.95, 26.85], a: "start" },
  { name: "Patna", ll: [85.14, 25.59], a: "start" },
  { name: "Guwahati", ll: [91.74, 26.14], a: "start" },
  { name: "Ahmedabad", ll: [72.57, 23.02], a: "end" },
  { name: "Kolkata", ll: [88.36, 22.57], a: "start" },
  { name: "Nagpur", ll: [79.09, 21.15], a: "start" },
  { name: "Bhubaneswar", ll: [85.82, 20.3], a: "start" },
  { name: "Mumbai", ll: [72.88, 19.08], a: "end" },
  { name: "Hyderabad", ll: [78.49, 17.39], a: "start" },
  { name: "Bengaluru", ll: [77.59, 12.97], a: "end" },
  { name: "Chennai", ll: [80.27, 13.08], a: "start" },
].map((c) => {
  const [x, y] = project(c.ll);
  return { ...c, x, y };
});

const DUR = 4; // seconds per loop
const STAGGER = 0.3;

// Curved path from origin to a destination
const buildPath = (d) => {
  const mx = (ORIGIN.x + d.x) / 2;
  const my = (ORIGIN.y + d.y) / 2;
  const dx = d.x - ORIGIN.x;
  const dy = d.y - ORIGIN.y;
  const len = Math.hypot(dx, dy) || 1;
  const bend = len * 0.2;
  const cx = mx + (-dy / len) * bend;
  const cy = my + (dx / len) * bend;
  return `M${ORIGIN.x} ${ORIGIN.y} Q${cx.toFixed(1)} ${cy.toFixed(1)} ${d.x} ${d.y}`;
};

export default function IndiaReachMap() {
  return (
    <div className="reach-map" aria-hidden="true">
      <svg viewBox="0 0 600 670" role="img" className="reach-map__svg">
        <defs>
          <linearGradient id="indiaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(200,155,60,0.20)" />
            <stop offset="100%" stopColor="rgba(200,155,60,0.06)" />
          </linearGradient>
        </defs>

        {/* Mainland + islands */}
        <path
          d={INDIA_PATH}
          className="reach-map__land"
          fill="url(#indiaFill)"
        />
        {ISLAND_PATHS.map((d, i) => (
          <path
            key={`isl-${i}`}
            d={d}
            className="reach-map__land"
            fill="url(#indiaFill)"
          />
        ))}

        {/* Arrows */}
        {DESTINATIONS.map((d, i) => {
          const path = buildPath(d);
          const delay = (i * STAGGER).toFixed(2);
          return (
            <g key={d.name}>
              <path
                d={path}
                pathLength="1"
                className="reach-map__route"
                style={{ animationDelay: `${delay}s` }}
              />
              <g className="reach-map__head" opacity="0">
                <polygon points="-7,-4.5 6,0 -7,4.5" />
                <animateMotion
                  dur={`${DUR}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                  rotate="auto"
                  path={path}
                  keyPoints="0;1;1"
                  keyTimes="0;0.6;1"
                  calcMode="linear"
                />
                <animate
                  attributeName="opacity"
                  dur={`${DUR}s`}
                  begin={`${delay}s`}
                  repeatCount="indefinite"
                  values="0;1;1;0"
                  keyTimes="0;0.05;0.9;1"
                />
              </g>
            </g>
          );
        })}

        {/* Destination dots + labels */}
        {DESTINATIONS.map((d) => (
          <g key={`dot-${d.name}`}>
            <circle cx={d.x} cy={d.y} r="4" className="reach-map__dot" />
            <text
              x={d.x + (d.a === "end" ? -9 : 9)}
              y={d.y + 4}
              textAnchor={d.a}
              className="reach-map__label"
            >
              {d.name}
            </text>
          </g>
        ))}

        {/* Origin: Palakkad */}
        <g>
          <circle
            cx={ORIGIN.x}
            cy={ORIGIN.y}
            r="8"
            className="reach-map__pulse"
          />
          <circle
            cx={ORIGIN.x}
            cy={ORIGIN.y}
            r="8"
            className="reach-map__pulse reach-map__pulse--2"
          />
          <circle
            cx={ORIGIN.x}
            cy={ORIGIN.y}
            r="6.5"
            className="reach-map__origin"
          />
          <text
            x={ORIGIN.x - 14}
            y={ORIGIN.y + 5}
            textAnchor="end"
            className="reach-map__label reach-map__label--origin"
          >
            Palakkad, Kerala
          </text>
        </g>
      </svg>
    </div>
  );
}
